import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import { ComponentProps, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { BookmarkPlus } from '@sorare/core/src/atoms/icons/BookmarkPlus';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { DropCards } from '@sorare/core/src/atoms/icons/DropCards';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { StackProps } from '@sorare/core/src/lib/algolia';
import { isSamePlayerSameSeason } from '@sorare/core/src/lib/cards';

import { AddTokenToDeck } from 'components/decks/AddTokenToDeck';
import { DeckEditorDialog } from 'components/decks/DeckEditorDialog';
import { Empty } from 'components/market/Empty';
import {
  AnimatedGrid,
  AnimatedGridItem,
  Grid,
  GridItem,
  GridOverlayLoadingIndicator,
} from 'components/market/Grid';
import {
  PlayerDetails,
  PlayerDrawer,
  Props as PlayerDrawerProps,
} from 'components/player/PlayerDrawer';
import { Token } from 'components/token/Token';
import { useMarketplaceContext } from 'contexts/Marketplace';
import { useCurrentMarketplaceUserContext } from 'contexts/currentMarketplaceUser';
import { useIsBoostAffordable } from 'hooks/useIsBoostAffordable';
import { useIsCardDroppable } from 'hooks/useIsCardDroppable';
import { MyCardsSelectionMode } from 'lib/my-cards';
import { CardResultsProps } from 'searchCards/AdvancedCardSearch/types';
import { Pagination } from 'searchCards/Pagination';

import { CardPreviewGrid_card } from './__generated__/index.graphql';

type Item = CardPreviewGrid_card & { stack?: StackProps };

type Props = {
  item: Item;
  setCardOpened: (cardSlug: string, playerSlug: string) => void;
} & Pick<
  ComponentProps<typeof Token>,
  | 'galleryOwnerSlug'
  | 'displayMarketplaceOnboardingTooltip'
  | 'hideOwner'
  | 'hideSorareUser'
  | 'stack'
  | 'hideDetails'
  | 'action'
  | 'onlyPrimary'
  | 'selectionMode'
  | 'totalXpNeeded'
  | 'onToggleCard'
  | 'selected'
  | 'forceShowActionButton'
  | 'isDoublonOfSelectedCardSlug'
>;

const TokenItem = ({ item, setCardOpened, ...tokenProps }: Props) => {
  const onInfoClick = useCallback(() => {
    setCardOpened(item.slug, item.anyPlayer.slug);
  }, [item, setCardOpened]);

  return (
    <Token
      card={item}
      onInfoClick={onInfoClick}
      stack={item.stack}
      {...tokenProps}
    />
  );
};

const getItemKey = (item: Item) => {
  return item.slug!;
};

type GridProps = Pick<
  CardResultsProps,
  'topic' | 'showDesktopFilter' | 'editableLists' | 'galleryOwnerSlug'
> & {
  items: Item[] | undefined;
  loading: boolean;
  PlayerDetailsComponent: PlayerDetails;
  sport: Sport;
  selectionMode?: MyCardsSelectionMode;
  setSelectionMode?: (mode: MyCardsSelectionMode) => void;
  selectedCardSlugs?: string[];
  canDrop?: boolean;
  canLevelUp?: boolean;
  showActionButtonOnFirtCard?: boolean;
} & Pick<
    ComponentProps<typeof TokenItem>,
    | 'galleryOwnerSlug'
    | 'hideOwner'
    | 'hideSorareUser'
    | 'onlyPrimary'
    | 'onToggleCard'
    | 'totalXpNeeded'
  >;

export const CardPreviewGrid = ({
  items,
  topic,
  loading,
  showDesktopFilter,
  editableLists,
  PlayerDetailsComponent,
  sport,
  selectionMode,
  setSelectionMode,
  onToggleCard,
  selectedCardSlugs,
  canDrop,
  canLevelUp,
  galleryOwnerSlug,
  showActionButtonOnFirtCard,
  ...tokenItemProps
}: GridProps) => {
  const { hideDetails } = useMarketplaceContext();
  const { userSportProfile } = useCurrentMarketplaceUserContext();
  const {
    flags: { useMlbSealing = false },
  } = useFeatureFlags();

  const isCardDroppable = useIsCardDroppable();
  const isBoostAffordable = useIsBoostAffordable();

  const [createListDialogOpen, setCreateListDialogOpen] = useState(false);
  const [addCardToDeck, setAddCardToDeck] =
    useState<CardPreviewGrid_card | null>(null);
  const [cardOpened, setCardOpened] =
    useState<PlayerDrawerProps['cardOpened']>(null);

  const { up: isTabletOrAbove } = useScreenSize('tablet');

  if (items?.length === 0 && !loading) {
    return <Empty isGallery={!!galleryOwnerSlug} topic={topic} />;
  }

  const flipKey = `${items?.map(item => getItemKey(item)).join('-')}${
    showDesktopFilter ? '-showDesktopFilter' : ''
  }`;

  const GridComponent = isTabletOrAbove ? AnimatedGrid : Grid;
  const GridItemComponent = isTabletOrAbove ? AnimatedGridItem : GridItem;

  return (
    <>
      <GridComponent
        staggerConfig={{
          default: {
            speed: 0.4,
          },
        }}
        flipKey={flipKey}
        className={classnames({
          twoColumnsOnMobile: !isTabletOrAbove,
          showDesktopFilter,
        })}
      >
        {items?.map((item, i) => {
          const noSelectionMode = !selectionMode;
          const showAddToDeck =
            noSelectionMode &&
            editableLists &&
            item.rarityTyped !== Rarity.common;

          const showDrop = noSelectionMode && canDrop && setSelectionMode;
          const showLevelUp = noSelectionMode && canLevelUp && setSelectionMode;
          const showSealing =
            (noSelectionMode ||
              (selectionMode === 'sealing' && item.sealable)) &&
            useMlbSealing &&
            item.sport === Sport.BASEBALL &&
            setSelectionMode;
          const { isDroppable } = isCardDroppable(item);
          const { isBoostable, xpNeeded } = isBoostAffordable(item);

          const isSelected = selectedCardSlugs?.includes(item.slug);

          const isDoublonOfSelectedCardSlug =
            !isSelected &&
            selectedCardSlugs?.some(selectedCardSlug =>
              isSamePlayerSameSeason(selectedCardSlug, item.slug)
            );

          return (
            <GridItemComponent key={getItemKey(item)} flipId={getItemKey(item)}>
              <TokenItem
                item={item}
                displayMarketplaceOnboardingTooltip={i === 0}
                forceShowActionButton={
                  (showActionButtonOnFirtCard && i === 0) ||
                  (showSealing && isSelected)
                }
                setCardOpened={(cardSlug, playerSlug) =>
                  setCardOpened({
                    cardSlug,
                    playerSlug,
                  })
                }
                hideDetails={!!(hideDetails && galleryOwnerSlug)}
                isDoublonOfSelectedCardSlug={isDoublonOfSelectedCardSlug}
                action={
                  showAddToDeck || showDrop || showLevelUp || showSealing ? (
                    <Vertical>
                      {showAddToDeck && (
                        <IconButton
                          onClick={() => {
                            if (!userSportProfile?.decks?.nodes?.length) {
                              setCreateListDialogOpen(true);
                            }
                            setAddCardToDeck(item);
                          }}
                          color="secondary"
                          small
                          disabled={!noSelectionMode}
                        >
                          <BookmarkPlus />
                        </IconButton>
                      )}
                      {showDrop && (
                        <OnboardingHighlightWrapper
                          wrap={
                            showActionButtonOnFirtCard &&
                            i === 0 &&
                            isTabletOrAbove
                          }
                          highlightName="DropCardCta"
                        >
                          <IconButton
                            onClick={() => {
                              setSelectionMode('drop');
                              if (isDroppable) {
                                onToggleCard?.(item.slug);
                              }
                            }}
                            color="secondary"
                            small
                            disabled={!noSelectionMode}
                          >
                            <DropCards size={2} />
                          </IconButton>
                        </OnboardingHighlightWrapper>
                      )}
                      {showLevelUp && (
                        <IconButton
                          onClick={() => {
                            setSelectionMode('boost');
                            if (isBoostable) {
                              onToggleCard?.(item.slug, { xpNeeded });
                            }
                          }}
                          color="secondary"
                          small
                          disabled={!noSelectionMode}
                        >
                          <Boost size={2} />
                        </IconButton>
                      )}
                      {showSealing && !isDoublonOfSelectedCardSlug && (
                        <ConditionalWrapper
                          Wrapper={Tooltip}
                          wrap={!item.sealable}
                          props={{
                            title: (
                              <FormattedMessage
                                id="Sealing.explanation.sealingDoublon"
                                defaultMessage="You already sealed the same edition of this card"
                              />
                            ),
                          }}
                        >
                          <IconButton
                            disabled={!item.sealable}
                            onClick={() => {
                              setSelectionMode('sealing');
                              onToggleCard?.(item.slug);
                            }}
                            color={isSelected ? 'primary' : 'secondary'}
                            small
                          >
                            {isSelected ? (
                              <FontAwesomeIcon icon={faCheck} />
                            ) : (
                              <Vault size={2} />
                            )}
                          </IconButton>
                        </ConditionalWrapper>
                      )}
                    </Vertical>
                  ) : undefined
                }
                selectionMode={selectionMode}
                selected={isSelected}
                onToggleCard={onToggleCard}
                galleryOwnerSlug={galleryOwnerSlug}
                {...tokenItemProps}
              />
            </GridItemComponent>
          );
        })}
      </GridComponent>
      <Pagination visible={!!items?.length} />
      {loading && <GridOverlayLoadingIndicator />}
      {addCardToDeck &&
        !createListDialogOpen &&
        addCardToDeck.rarityTyped !== Rarity.common && (
          <AddTokenToDeck
            card={addCardToDeck}
            onClose={() => setAddCardToDeck(null)}
            addList={() => setCreateListDialogOpen(true)}
          />
        )}
      {createListDialogOpen && (
        <DeckEditorDialog
          sport={sport}
          onClose={() => {
            setAddCardToDeck(addCardToDeck);
            setCreateListDialogOpen(false);
          }}
        />
      )}
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
        PlayerDetails={PlayerDetailsComponent}
      />
    </>
  );
};

CardPreviewGrid.fragments = {
  card: gql`
    fragment CardPreviewGrid_card on AnyCardInterface {
      slug
      sealable
      sport
      anyPlayer {
        slug
      }
      ...AddTokenToDeck_anyCard @include(if: $editableLists)
      ...Token_anyCard
      ...useIsCardDroppable_anyCard
      ...useIsBoostAffordable_anyCard
    }
    ${Token.fragments.anyCard}
    ${AddTokenToDeck.fragments.anyCard}
    ${useIsCardDroppable.fragments.anyCard}
    ${useIsBoostAffordable.fragments.anyCard}
  ` as TypedDocumentNode<CardPreviewGrid_card>,
};
